import { useState, useEffect, useMemo } from 'react';
import api from './api';
import { formatDate } from './FreelancersPage';

// ─── TeamCalendarPage ───────────────────────────────────────────────────────────
// Адмінський/командний календар (Admin, SuperAdmin, HR, PM) — на відміну від
// MyCalendarPage.js (особистий календар фрілансера), тут завжди показуються ВСІ
// проєкти системи + зайнятість усіх фрілансерів + дні народження + терміни
// документів + особисті нотатки фрілансерів (read-only для адміна — сам
// фрілансер керує своїми нотатками лише зі свого календаря).
//
// Два режими перегляду: Month (місячна сітка з перемикачами-шарами) і
// Team (рядок на кожного фрілансера, "теплова карта" зайнятості по днях —
// найшвидший спосіб відповісти на "хто вільний у цей період").

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS    = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const STATUS_COLOR = {
  Planning:   'var(--text-dim)',
  Confirmed:  'var(--success-text)',
  Active:     'var(--success-text)',
  'On Hold':  'var(--warning-text)',
  Completed:  'var(--purple-text)',
  Canceled:   'var(--accent)',
};

const LAYERS = {
  projects:  { label: 'Projects',            color: 'var(--info-text)', icon: '📋' },
  occupancy: { label: 'Freelancer occupancy', color: 'var(--success-text)', icon: '👥' },
  // Дні, явно позначені адміном як travel у формі проєкту (AssignedFreelancersBlock,
  // a.travelDates) — окремий шар, щоб можна було вимкнути незалежно від
  // загальної зайнятості. Раніше (до 04.08.2026) перший/останній день
  // періоду ВВАЖАВСЯ travel-днем автоматично; тепер це явний вибір адміна,
  // за замовчуванням — жодного travel-дня.
  travel:    { label: 'Travel days',          color: 'var(--warning-text)', icon: '✈️' },
  birthdays: { label: 'Birthdays',            color: 'var(--purple-text)', icon: '🎂' },
  expiries:  { label: 'Document expiries',    color: 'var(--warning-text)', icon: '⚠️' },
  notes:     { label: 'Freelancer notes',     color: 'var(--accent)', icon: '📝' },
};
const LAYER_KEYS = Object.keys(LAYERS);

// ─── Date helpers (той самий локальний парсинг, що й в MyCalendarPage.js —
// без зсуву часового поясу) ───────────────────────────────────────────────────
function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function toKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function forEachDay(start, end, cb) {
  if (!start) return;
  const cur = new Date(start);
  const last = end && end >= start ? end : start;
  // Захист від випадково величезних діапазонів (напр. відсутня end date) —
  // не більше ~3 років, цього достатньо для будь-якого реального проєкту.
  let guard = 0;
  while (cur <= last && guard < 1100) {
    cb(toKey(cur));
    cur.setDate(cur.getDate() + 1);
    guard++;
  }
}

export default function TeamCalendarPage({ onBack, projects = [], freelancers = [] }) {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState('');
  const [cursor,  setCursor]    = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'team'
  const [activeLayers, setActiveLayers] = useState(new Set(LAYER_KEYS));
  const [freelancerFilter, setFreelancerFilter] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.freelancers.allCalendarEntries()
      .then(res => setEntries(res.entries || []))
      .catch(e => setError(e.message || 'Failed to load calendar entries'))
      .finally(() => setLoading(false));
  }, []);

  const toggleLayer = key => setActiveLayers(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const year  = cursor.getFullYear();
  const month = cursor.getMonth();

  // ── Агрегація по днях: projects / occupancy / birthdays / expiries / notes ──
  const dayData = useMemo(() => {
    const map = {};
    const ensure = key => map[key] || (map[key] = { projects: [], occupancy: [], travel: [], birthdays: [], expiries: [], notes: [] });

    projects.forEach(p => {
      if (p.archived) return;
      const freelancersAssigned = (p.assigned || []).filter(a => !a.isAdmin);
      if (freelancerFilter && !freelancersAssigned.some(a => String(a.id) === String(freelancerFilter))) return;

      const pStart = parseDate(p.startDate);
      const pEnd   = parseDate(p.endDate) || pStart;
      if (pStart) forEachDay(pStart, pEnd, key => ensure(key).projects.push(p));

      freelancersAssigned.forEach(a => {
        if (freelancerFilter && String(a.id) !== String(freelancerFilter)) return;
        const start = parseDate(a.dateFrom) || pStart;
        const end   = parseDate(a.dateTo)   || pEnd || start;
        if (!start) return;
        const startKey = toKey(start);
        const endKey   = toKey(end);
        const travelDates = new Set(Array.isArray(a.travelDates) ? a.travelDates : []);
        forEachDay(start, end, key => {
          const isTravelDay = travelDates.has(key);
          const direction = key === startKey && key === endKey ? 'both' : key === startKey ? 'arrival' : key === endKey ? 'departure' : 'travel';
          const entry = { freelancerId: a.id, freelancerName: a.name, project: p, isTravelDay, direction };
          ensure(key).occupancy.push(entry);
          if (isTravelDay) ensure(key).travel.push(entry);
        });
      });
    });

    freelancers.forEach(f => {
      if (freelancerFilter && String(f.id) !== String(freelancerFilter)) return;

      if (f.dob) {
        const b = parseDate(f.dob);
        if (b) {
          const occurrence = new Date(year, b.getMonth(), b.getDate());
          if (occurrence.getMonth() === b.getMonth()) {
            ensure(toKey(occurrence)).birthdays.push({ freelancer: f, age: year - b.getFullYear() });
          }
        }
      }
      const pushExpiry = (dateStr, label) => {
        const d = parseDate(dateStr);
        if (d) ensure(toKey(d)).expiries.push({ freelancer: f, label, date: dateStr });
      };
      if (f.passportExpiry) pushExpiry(f.passportExpiry, 'Passport');
      (f.additionalPassports || []).forEach(ap => ap.expiry && pushExpiry(ap.expiry, `Passport (${ap.number || '?'})`));
      (f.documents || []).forEach(doc => doc.expiry && pushExpiry(doc.expiry, doc.type || 'Document'));
      (f.visas || []).forEach(v => v.expiry && pushExpiry(v.expiry, `Visa (${v.country || '?'})`));
    });

    entries.forEach(en => {
      if (freelancerFilter && String(en.freelancerId) !== String(freelancerFilter)) return;
      const start = parseDate(en.dateFrom);
      const end   = parseDate(en.dateTo) || start;
      if (!start) return;
      forEachDay(start, end, key => ensure(key).notes.push(en));
    });

    return map;
  }, [projects, freelancers, entries, year, freelancerFilter]);

  const cells = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const leadingBlank = (firstOfMonth.getDay() + 6) % 7; // Понеділок = 0
    const daysInMonth  = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < leadingBlank; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    return arr;
  }, [year, month]);

  const today = new Date();
  const selectedData = selectedDay ? (dayData[toKey(selectedDay)] || { projects: [], occupancy: [], travel: [], birthdays: [], expiries: [], notes: [] }) : null;

  // ── Team view: freelancer × день, статус зайнятості ──────────────────────────
  const teamRows = useMemo(() => {
    if (viewMode !== 'team') return [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const list = freelancerFilter ? freelancers.filter(f => String(f.id) === String(freelancerFilter)) : freelancers;
    return list.map(f => {
      const days = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const key = toKey(new Date(year, month, d));
        const dd = dayData[key];
        const occ  = dd ? dd.occupancy.find(o => String(o.freelancerId) === String(f.id)) : null;
        const note = dd ? dd.notes.find(n => String(n.freelancerId) === String(f.id)) : null;
        if (note && note.type === 'unavailable') days.push({ state: 'unavailable', title: note.title || 'Unavailable' });
        else if (occ && occ.isTravelDay) days.push({ state: 'travel', title: `✈️ Travel day — ${occ.project?.name || 'Project'}` });
        else if (occ) days.push({ state: 'busy', title: occ.project?.name || 'Project' });
        else if (note) days.push({ state: 'note', title: note.title || 'Note' });
        else days.push({ state: 'free', title: '' });
      }
      return { freelancer: f, days };
    });
  }, [viewMode, freelancers, freelancerFilter, dayData, year, month]);

  const inputStyle = { background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '7px 12px', fontSize: '13px', outline: 'none' };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>
      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back
          </button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>TEAM CALENDAR</span>
        </div>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', padding: '4px' }}>
          {['month', 'team'].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              style={{
                background: viewMode === m ? 'var(--border)' : 'transparent', border: 'none',
                color: viewMode === m ? 'var(--text-primary)' : 'var(--text-faint)', padding: '6px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', textTransform: 'capitalize',
              }}>
              {m === 'month' ? 'Month' : 'Team'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: viewMode === 'team' ? '1400px' : '1000px', margin: '0 auto' }}>
        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Toolbar: layer toggles + freelancer filter */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '18px' }}>
          {LAYER_KEYS.map(key => {
            const l = LAYERS[key];
            const on = activeLayers.has(key);
            return (
              <button key={key} onClick={() => toggleLayer(key)}
                style={{
                  background: on ? `${l.color}22` : 'transparent', border: `1px solid ${on ? l.color : 'var(--border-strong)'}`,
                  color: on ? l.color : 'var(--text-dim)', padding: '5px 12px', borderRadius: '999px', fontSize: '11px',
                  fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                <span>{l.icon}</span>{l.label}
              </button>
            );
          })}
          <select style={{ ...inputStyle, marginLeft: 'auto' }} value={freelancerFilter} onChange={e => setFreelancerFilter(e.target.value)}>
            <option value="">All freelancers</option>
            {freelancers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>

        {/* Month nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <button onClick={() => setCursor(new Date(year, month - 1, 1))}
            style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', color: 'var(--text-body)', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}>←</button>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '17px', margin: 0 }}>{MONTH_NAMES[month]} {year}</h2>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))}
            style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', color: 'var(--text-body)', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}>→</button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</p>
        ) : viewMode === 'month' ? (
          <>
            {/* Weekday header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px', marginBottom: '6px' }}>
              {WEEKDAYS.map(w => (
                <div key={w} style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{w}</div>
              ))}
            </div>

            {/* Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
              {cells.map((date, i) => {
                if (!date) return <div key={`b${i}`} />;
                const key = toKey(date);
                const dd  = dayData[key] || { projects: [], occupancy: [], travel: [], birthdays: [], expiries: [], notes: [] };
                const occCount    = new Set(dd.occupancy.map(o => o.freelancerId)).size;
                const travelCount = new Set(dd.travel.map(o => o.freelancerId)).size;
                const isToday = isSameDay(date, today);
                const isSelected = selectedDay && isSameDay(date, selectedDay);
                const dots = [
                  activeLayers.has('projects')  && dd.projects.length > 0  && { key: 'projects',  count: dd.projects.length },
                  activeLayers.has('occupancy') && occCount > 0            && { key: 'occupancy', count: occCount },
                  activeLayers.has('travel')    && travelCount > 0         && { key: 'travel',     count: travelCount },
                  activeLayers.has('birthdays') && dd.birthdays.length > 0 && { key: 'birthdays', count: dd.birthdays.length },
                  activeLayers.has('expiries')  && dd.expiries.length > 0  && { key: 'expiries',  count: dd.expiries.length },
                  activeLayers.has('notes')     && dd.notes.length > 0     && { key: 'notes',     count: dd.notes.length },
                ].filter(Boolean);
                return (
                  <div key={key}
                    onClick={() => setSelectedDay(date)}
                    style={{
                      minHeight: '72px', borderRadius: '8px', padding: '6px', cursor: 'pointer',
                      background: isSelected ? 'var(--bg-hover)' : 'var(--bg-panel)',
                      border: isToday ? '1px solid var(--accent)' : '1px solid var(--bg-subtle)',
                    }}>
                    <div style={{ fontSize: '12px', color: isToday ? 'var(--accent)' : 'var(--text-faint)', marginBottom: '6px' }}>{date.getDate()}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {dots.map(d => (
                        <span key={d.key} title={LAYERS[d.key].label} style={{
                          background: `${LAYERS[d.key].color}22`, color: LAYERS[d.key].color,
                          border: `1px solid ${LAYERS[d.key].color}`, borderRadius: '4px',
                          fontSize: '9px', fontWeight: 'bold', padding: '1px 4px', lineHeight: '1.4',
                        }}>
                          {LAYERS[d.key].icon}{d.count}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Selected day detail */}
            {selectedDay && selectedData && (
              <div style={{ marginTop: '24px', background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px' }}>
                <h3 style={{ color: 'var(--text-primary)', fontSize: '14px', margin: '0 0 14px' }}>
                  {selectedDay.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>

                {Object.values(selectedData).every(arr => arr.length === 0) && (
                  <p style={{ color: 'var(--text-dim)', fontSize: '12px', margin: 0 }}>Nothing on this day.</p>
                )}

                {activeLayers.has('projects') && selectedData.projects.length > 0 && (
                  <DetailSection title="Projects" color={LAYERS.projects.color}>
                    {selectedData.projects.map(p => {
                      const onProject = selectedData.occupancy.filter(o => o.project.id === p.id);
                      return (
                        <div key={p.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{p.name}</span>
                            <span style={{ color: STATUS_COLOR[p.status] || 'var(--text-muted)', fontSize: '11px', fontWeight: 'bold' }}>{p.status}</span>
                          </div>
                          {onProject.length > 0 && (
                            <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '2px' }}>
                              👥 {onProject.map(o => o.freelancerName + (o.isTravelDay ? ' ✈️' : '')).join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </DetailSection>
                )}

                {activeLayers.has('travel') && selectedData.travel.length > 0 && (
                  <DetailSection title="Travel days" color={LAYERS.travel.color}>
                    {selectedData.travel.map((t, i) => (
                      <div key={i} style={{ padding: '4px 0', color: 'var(--text-body)', fontSize: '13px' }}>
                        ✈️ {t.freelancerName} — {t.direction === 'both' ? 'arrival & departure' : t.direction === 'arrival' ? 'arrival' : t.direction === 'departure' ? 'departure' : 'travel day'} ({t.project?.name || 'Project'})
                      </div>
                    ))}
                  </DetailSection>
                )}

                {activeLayers.has('birthdays') && selectedData.birthdays.length > 0 && (
                  <DetailSection title="Birthdays" color={LAYERS.birthdays.color}>
                    {selectedData.birthdays.map((b, i) => (
                      <div key={i} style={{ padding: '4px 0', color: 'var(--text-body)', fontSize: '13px' }}>🎂 {b.freelancer.name} — turns {b.age}</div>
                    ))}
                  </DetailSection>
                )}

                {activeLayers.has('expiries') && selectedData.expiries.length > 0 && (
                  <DetailSection title="Document expiries" color={LAYERS.expiries.color}>
                    {selectedData.expiries.map((e, i) => (
                      <div key={i} style={{ padding: '4px 0', color: 'var(--text-body)', fontSize: '13px' }}>{e.freelancer.name} — {e.label} ({formatDate(e.date)})</div>
                    ))}
                  </DetailSection>
                )}

                {activeLayers.has('notes') && selectedData.notes.length > 0 && (
                  <DetailSection title="Freelancer notes" color={LAYERS.notes.color}>
                    {selectedData.notes.map(n => (
                      <div key={n.id} style={{ padding: '4px 0', color: 'var(--text-body)', fontSize: '13px' }}>
                        {n.type === 'unavailable' ? '🚫' : '📝'} {n.freelancerName} — {n.title || (n.type === 'unavailable' ? 'Unavailable' : 'Note')}
                      </div>
                    ))}
                  </DetailSection>
                )}
              </div>
            )}
          </>
        ) : (
          /* ── Team view: теплова карта зайнятості ────────────────────────── */
          <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'auto' }}>
            {teamRows.length === 0 ? (
              <p style={{ color: 'var(--text-dim)', fontSize: '13px', padding: '20px' }}>No freelancers to show.</p>
            ) : (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', left: 0, background: 'var(--bg-panel)', color: 'var(--text-muted)', fontSize: '11px', textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Freelancer</th>
                    {teamRows[0].days.map((_, i) => {
                      const d = new Date(year, month, i + 1);
                      const isToday = isSameDay(d, today);
                      return (
                        <th key={i} style={{ color: isToday ? 'var(--accent)' : 'var(--text-dim)', fontSize: '10px', padding: '10px 4px', borderBottom: '1px solid var(--border)', minWidth: '22px' }}>{i + 1}</th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {teamRows.map(row => (
                    <tr key={row.freelancer.id}>
                      <td style={{ position: 'sticky', left: 0, background: 'var(--bg-panel)', color: 'var(--text-body)', fontSize: '12px', padding: '6px 12px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--bg-hover)' }}>{row.freelancer.name}</td>
                      {row.days.map((d, i) => {
                        const cfg = {
                          busy:        { bg: 'var(--success-bg)', color: 'var(--success-text)' },
                          travel:      { bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
                          unavailable: { bg: 'var(--danger-bg)', color: 'var(--accent)' },
                          note:        { bg: 'var(--info-bg)', color: 'var(--info-text)' },
                          free:        { bg: 'var(--bg-card)', color: 'transparent' },
                        }[d.state];
                        return (
                          <td key={i} title={d.title} style={{ padding: '3px', borderBottom: '1px solid var(--bg-hover)' }}>
                            <div style={{ height: '16px', borderRadius: '3px', background: cfg.bg, border: `1px solid ${cfg.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', lineHeight: 1 }}>
                              {d.state === 'travel' ? '✈️' : ''}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ display: 'flex', gap: '18px', padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
              <Legend color="var(--success-text)" label="Busy (project)" />
              <Legend color="var(--warning-text)" label="✈️ Travel day" />
              <Legend color="var(--accent)" label="Unavailable" />
              <Legend color="var(--info-text)" label="Note" />
              <Legend color="var(--bg-card)" border label="Free" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailSection({ title, color, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <p style={{ color, fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px' }}>{title}</p>
      {children}
    </div>
  );
}

function Legend({ color, label, border }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: color, border: border ? '1px solid var(--border-strong)' : 'none' }} />
      <span style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{label}</span>
    </div>
  );
}
